/**
 * Simple Product Shipping Integration Script
 * 
 * This script handles integration between product variant changes and the simple shipping calculator.
 * It operates completely independently from cart functionality to avoid conflicts.
 * 
 * Key Features:
 * - Listens for variant changes on product pages
 * - Updates shipping calculator when variants change
 * - Handles quantity changes from product form
 * - Maintains complete isolation from cart operations
 * - Works with existing product form functionality
 */

class SimpleProductShippingIntegration {
  constructor(options = {}) {
    this.calculator = null;
    this.productForm = null;
    this.variantInput = null;
    this.quantityInput = null;
    this.initialized = false;
    this.debug = options.debug || false;
    this.lastVariantId = null;
    this.lastQuantity = null;
    
    // Bind methods to maintain context
    this.handleVariantChange = this.handleVariantChange.bind(this);
    this.handleQuantityChange = this.handleQuantityChange.bind(this);
    this.handleFormChange = this.handleFormChange.bind(this);
    
    this.init();
  }

  init() {
    // Wait for DOM to be ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.setup());
    } else {
      this.setup();
    }
  }

  setup() {
    try {
      if (this.debug) console.log('Setting up simple product shipping integration...');
      
      // Find the shipping calculator
      this.calculator = window.simpleProductShippingCalculator;
      if (!this.calculator) {
        if (this.debug) console.log('Simple product shipping calculator not found, integration disabled');
        return;
      }

      // Find the product form
      this.productForm = document.querySelector('form[action*="/cart/add"]');
      if (!this.productForm) {
        if (this.debug) console.log('Product form not found, shipping integration disabled');
        return;
      }

      // Find variant and quantity inputs
      this.variantInput = this.productForm.querySelector('input[name="id"], select[name="id"]');
      this.quantityInput = this.productForm.querySelector('input[name="quantity"]');

      if (!this.variantInput) {
        if (this.debug) console.log('Variant input not found, shipping integration disabled');
        return;
      }

      // Store initial values
      this.lastVariantId = this.getCurrentVariantId();
      this.lastQuantity = this.getCurrentQuantity();

      this.bindEvents();
      this.initialized = true;
      
      if (this.debug) {
        console.log('Simple product shipping integration initialized successfully');
        console.log('Initial variant ID:', this.lastVariantId);
        console.log('Initial quantity:', this.lastQuantity);
      }
    } catch (error) {
      console.error('Error initializing simple product shipping integration:', error);
    }
  }

  bindEvents() {
    // Listen for variant changes
    if (this.variantInput) {
      this.variantInput.addEventListener('change', this.handleVariantChange);
    }

    // Listen for quantity changes
    if (this.quantityInput) {
      this.quantityInput.addEventListener('input', this.handleQuantityChange);
      this.quantityInput.addEventListener('change', this.handleQuantityChange);
    }

    // Listen for any form changes (covers custom variant selectors)
    this.productForm.addEventListener('change', this.handleFormChange);

    // Listen for custom events that might be triggered by theme's variant picker
    document.addEventListener('variant:change', this.handleVariantChange);
    document.addEventListener('product:variant-change', this.handleVariantChange);
    document.addEventListener('product:quantity-change', this.handleQuantityChange);

    // Listen for Shopify's built-in variant change events
    document.addEventListener('shopify:variant:change', this.handleVariantChange);

    // Listen for theme-specific events (common patterns)
    document.addEventListener('variantChange', this.handleVariantChange);
    document.addEventListener('productVariantChange', this.handleVariantChange);

    // Listen for cart update events (from custom-qty.js)
    document.addEventListener('cart:updated', this.handleQuantityChange);
    document.addEventListener('cartdrawer:opened', this.handleQuantityChange);

    // Listen for product form updates (covers most theme implementations)
    const productFormElements = document.querySelectorAll('product-form');
    productFormElements.forEach(form => {
      form.addEventListener('variant:change', this.handleVariantChange);
      form.addEventListener('quantity:change', this.handleQuantityChange);
    });

    // Listen for variant picker updates (multiple selector types)
    const variantPickers = document.querySelectorAll('[data-option-index], .variant-input-wrapper, .product-form__input');
    variantPickers.forEach(picker => {
      const inputs = picker.querySelectorAll('input[type="radio"], input[type="checkbox"], select, input[name*="option"]');
      inputs.forEach(input => {
        input.addEventListener('change', this.handleVariantChange);
      });
    });

    // Also listen for any input that might affect variant selection
    const allVariantInputs = document.querySelectorAll('input[name="id"], select[name="id"], input[data-variant-input], select[data-variant-input]');
    allVariantInputs.forEach(input => {
      input.addEventListener('change', this.handleVariantChange);
    });

    // Listen for quantity selector updates
    const quantitySelectors = document.querySelectorAll('.quantity-selector, .product-form__quantity');
    quantitySelectors.forEach(selector => {
      const inputs = selector.querySelectorAll('input, select, button');
      inputs.forEach(input => {
        if (input.type === 'button') {
          input.addEventListener('click', () => {
            // Use setTimeout to ensure quantity value has been updated
            setTimeout(this.handleQuantityChange, 100);
          });
        } else {
          input.addEventListener('change', this.handleQuantityChange);
          input.addEventListener('input', this.handleQuantityChange);
        }
      });
    });
  }

  handleVariantChange(event) {
    if (!this.initialized || !this.calculator) return;

    try {
      // Get the new variant ID
      const newVariantId = this.getCurrentVariantId();
      
      if (this.debug) {
        console.log('Variant change detected:', {
          event: event?.type,
          oldVariantId: this.lastVariantId,
          newVariantId: newVariantId
        });
      }
      
      if (newVariantId && newVariantId !== this.lastVariantId) {
        this.lastVariantId = newVariantId;
        
        // Update the calculator for the new variant
        if (typeof this.calculator.updateVariant === 'function') {
          this.calculator.updateVariant();
          
          if (this.debug) {
            console.log('Shipping calculator updated for variant:', newVariantId);
          }
        }
      }
    } catch (error) {
      console.error('Error handling variant change:', error);
    }
  }

  handleQuantityChange(event) {
    if (!this.initialized || !this.calculator) return;

    try {
      const newQuantity = this.getCurrentQuantity();
      
      if (this.debug) {
        console.log('Quantity change detected:', {
          event: event?.type,
          oldQuantity: this.lastQuantity,
          newQuantity: newQuantity
        });
      }
      
      // Update the calculator for the new quantity
      if (typeof this.calculator.updateQuantity === 'function') {
        this.calculator.updateQuantity();
        this.lastQuantity = newQuantity;
        
        if (this.debug) {
          console.log('Shipping calculator updated for quantity:', newQuantity);
        }
      }
    } catch (error) {
      console.error('Error handling quantity change:', error);
    }
  }

  handleFormChange(event) {
    if (!this.initialized || !this.calculator) return;

    // Check if this is a variant-related change
    const target = event.target;
    if (!target) return;

    const isVariantChange = target.name === 'id' || 
                           target.closest('[data-option-index]') ||
                           target.classList.contains('variant-input') ||
                           target.hasAttribute('data-variant-input');

    const isQuantityChange = target.name === 'quantity' ||
                            target.classList.contains('quantity-input') ||
                            target.closest('.quantity-selector') ||
                            target.closest('.product-form__quantity');

    if (isVariantChange) {
      // Use setTimeout to ensure the variant has been processed
      setTimeout(this.handleVariantChange, 50);
    } else if (isQuantityChange) {
      // Use setTimeout to ensure the quantity has been updated
      setTimeout(this.handleQuantityChange, 50);
    }
  }

  getCurrentVariantId() {
    if (!this.variantInput) return null;
    
    try {
      return this.variantInput.value;
    } catch (error) {
      console.error('Error getting current variant ID:', error);
      return null;
    }
  }

  getCurrentQuantity() {
    if (!this.quantityInput) return null;
    
    try {
      return parseInt(this.quantityInput.value) || 1;
    } catch (error) {
      console.error('Error getting current quantity:', error);
      return null;
    }
  }

  // Public method to manually trigger updates (for theme developers)
  updateShippingCalculator() {
    if (!this.initialized || !this.calculator) return;

    try {
      if (typeof this.calculator.updateVariant === 'function') {
        this.calculator.updateVariant();
      }
    } catch (error) {
      console.error('Error manually updating shipping calculator:', error);
    }
  }

  // Public method to check if integration is working
  isActive() {
    return this.initialized && this.calculator !== null;
  }

  // Public method to get current product data (for debugging)
  getProductData() {
    return {
      variantId: this.getCurrentVariantId(),
      quantity: this.getCurrentQuantity(),
      formFound: !!this.productForm,
      calculatorFound: !!this.calculator,
      initialized: this.initialized
    };
  }
}

// Initialize the integration when the script loads
let simpleProductShippingIntegration;

// Wait for the shipping calculator to be available
function initializeIntegration() {
  // Check if we have both the calculator class and instance
  if (typeof SimpleProductShippingCalculator !== 'undefined' && 
      (window.simpleProductShippingCalculator || document.getElementById('simple-product-shipping-calculator'))) {
    
    // If we have the class but not the instance, wait a bit more
    if (!window.simpleProductShippingCalculator) {
      setTimeout(initializeIntegration, 200);
      return;
    }
    
    const debugMode = window.simpleProductShippingIntegrationDebug || false;
    simpleProductShippingIntegration = new SimpleProductShippingIntegration({ debug: debugMode });
    window.simpleProductShippingIntegration = simpleProductShippingIntegration;
  } else {
    // Retry after a short delay, but don't retry indefinitely
    if (!initializeIntegration.attempts) {
      initializeIntegration.attempts = 0;
    }
    
    initializeIntegration.attempts++;
    
    if (initializeIntegration.attempts < 50) { // Max 5 seconds of retries
      setTimeout(initializeIntegration, 100);
    } else {
      console.log('Simple product shipping integration: Calculator not found after 5 seconds, giving up');
    }
  }
}

// Start initialization
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initializeIntegration);
} else {
  initializeIntegration();
}

// Make integration available globally for debugging and theme developers
window.simpleProductShippingIntegration = simpleProductShippingIntegration;

// Export for module systems (if needed)
if (typeof module !== 'undefined' && module.exports) {
  module.exports = SimpleProductShippingIntegration;
}